/* ALPHX Bundle Builder — V1 Native Shopify (Automatic Discounts)
   Source of truth for Alpine component: x-data="bundleBuilder"

   Notes:
   - Uses /cart/add.js multi-add with { items: [...] }
   - No discount codes. Shopify Automatic Discounts apply based on quantity in "bundle-builder" collection (or your automatic discount rules).
   - Adds optional line item properties for grouping/analytics only (does NOT drive discount logic).
*/
function bundleBuilder() {
  return {
    // -------------------------
    // state
    // -------------------------
    products: [],
    tier: 3,
    slots: [],
    filterFit: '',
    filterStyle: '',
    filterSize: '',
    filterColor: '',
    quickSelectProduct: null,
    checkoutLoading: false,

    // bundle identifiers (theme-side only, for grouping)
    bundleId: null,

    // -------------------------
    // computed
    // -------------------------
    get tierOptions() {
      return [
        { count: 3, label: '3 items — 20% off' },
        { count: 5, label: '5 items — 25% off' },
        { count: 7, label: '7 items — 30% off' }
      ];
    },

    get slotArray() {
      return this.slots;
    },

    get filledCount() {
      return this.slots.filter(Boolean).length;
    },

    get progressPercent() {
      return this.tier ? Math.round((this.filledCount / this.tier) * 100) : 0;
    },

    get discountPercent() {
      const pct = { 3: '20%', 5: '25%', 7: '30%' };
      return pct[this.tier] || '';
    },

    // -------------------------
// Phase 6A — source-of-truth UX state (quantity-based, not tier-based)
// -------------------------
get count() {
  return this.filledCount;
},

get savingsTier() {
  const c = this.count;
  if (c >= 7) return '30';
  if (c >= 5) return '25';
  if (c >= 3) return '20';
  return 'none';
},

get nextSavingsTier() {
  const c = this.count;
  if (c < 3) return '20';
  if (c < 5) return '25';
  if (c < 7) return '30';
  return null;
},

/* ✅ Step 3.1 — Live savings indicator for sticky bar */
get savingsText() {
  const c = this.count;
  if (c >= 7) return 'Set Savings: 30%';
  if (c >= 5) return 'Set Savings: 25%';
  if (c >= 3) return 'Set Savings: 20%';
  return 'Set Savings: —';
},

copy() {
  const c = this.count;

  const progressLabel =
    c < 3 ? 'Select 3 items to unlock 20% savings' :
    c < 5 ? 'Add 1–2 more items to save 25%' :
    c < 7 ? 'Add 1–2 more items to save 30%' :
            'Maximum ALPHX Set Savings unlocked';

  const upsellLabelBase =
    c < 3 ? `Add ${3 - c} more to save 20%` :
    c < 5 ? `Add ${5 - c} more to save 25%` :
    c < 7 ? `Add ${7 - c} more to save 30%` :
            'Maximum ALPHX Set Savings Applied';

  // Show hint only when tapping the pill will expand tier (3–6 items)
  const canExpand = (c >= 3 && c < 7);

  const upsellLabel = canExpand
    ? `${upsellLabelBase} · Tap to expand`
    : upsellLabelBase;

  const ctaLabel =
    c < 3 ? 'Add items to unlock savings' :
            'Add Your Set to Cart';

  return { progressLabel, upsellLabel, ctaLabel };
},

    get progressLabel() {
      return this.copy().progressLabel;
    },

    get upsellLabel() {
      return this.copy().upsellLabel;
    },

    get ctaLabel() {
      return this.copy().ctaLabel;
    },

    get progressPct() {
      // Premium-feel mapping (fast early progress)
      const c = this.count;
      const map = { 0: 0, 1: 22, 2: 45, 3: 60, 4: 70, 5: 82, 6: 91 };
      return c >= 7 ? 100 : (map[c] ?? 0);
    },

    // -------------------------
    // init
    // -------------------------
    init() {
      // Load products (from bundle-data snippet)
      const raw = window.bundleProducts || window.ALPHX_BUNDLE_DATA;

      if (typeof raw === 'string') {
        try {
          this.products = JSON.parse(raw);
        } catch (e) {
          console.error('[ALPHX Bundle Builder] init: Failed to parse bundle data:', e);
          this.products = [];
        }
      } else {
        this.products = Array.isArray(raw) ? raw : [];
      }

      // Init bundle id + slots
      this.bundleId = this.newBundleId();
      this.setTier(this.tier, { silent: true });

      // Debug
      let variantCount = 0;
      this.products.forEach((p) => { variantCount += (p.variants && p.variants.length) || 0; });
      console.log('[ALPHX Bundle Builder] init – products:', this.products.length, '| variants:', variantCount);

// Set CSS variable for sticky bar height (prevents overlap + positions floating message)
this.$nextTick(() => {
  const bar = document.querySelector('.alphx-setbar');
  if (!bar) return;

  const setVar = () => {
    const h = Math.ceil(bar.getBoundingClientRect().height || 86);
    document.documentElement.style.setProperty('--alphx-setbar-h', `${h}px`);
  };

  setVar();
  window.addEventListener('resize', setVar);
});

},

    // -------------------------
    // tier + slots
    // -------------------------
    setTier(nextTier, opts = {}) {
      const preserved = this.slots.filter(Boolean); // keep filled entries in order
      this.tier = Number(nextTier);

      // Rebuild to exact tier length
      this.slots = Array(this.tier).fill(null);

      // Restore preserved up to tier
      for (let i = 0; i < Math.min(preserved.length, this.tier); i++) {
        this.slots[i] = preserved[i];
      }

      if (!opts.silent && preserved.length > this.tier) {
        console.log('[ALPHX Bundle Builder] Tier reduced; extra items removed.');
      }
    },

    removeSlot(index) {
      this.slots = this.slots.map((s, i) => (i === index ? null : s));
    },

    clearBundle() {
      this.slots = Array(this.tier).fill(null);
      this.bundleId = this.newBundleId();
    },

    // -------------------------
    // filter option lists
    // -------------------------
    get styleOptions() {
      const set = new Set();
      this.products.forEach((p) => { if (p.style && String(p.style).trim()) set.add(p.style); });
      return Array.from(set).sort();
    },

    get sizeOptions() {
      const set = new Set();
      this.products.forEach((p) => {
        (p.variants || []).forEach((v) => {
          const size = (v.size || '').trim() || (parseVariantTitle(v.title)[0] || '').trim();
          if (size) set.add(size);
        });
      });
      return Array.from(set).sort();
    },

    get colorOptions() {
      const set = new Set();
      this.products.forEach((p) => {
        (p.variants || []).forEach((v) => {
          const color = (v.color || '').trim() || (parseVariantTitle(v.title)[1] || '').trim();
          if (color) set.add(color);
        });
      });
      return Array.from(set).sort();
    },

    filteredProducts() {
      // If you have a custom filter hook, use it
      if (typeof window.ALPHX_BUNDLE_FILTER_PRODUCTS === 'function') {
        return window.ALPHX_BUNDLE_FILTER_PRODUCTS(this.products, {
          fit: this.filterFit || undefined,
          style: this.filterStyle || undefined,
          size: this.filterSize || undefined,
          color: this.filterColor || undefined
        });
      }

      let list = this.products;

      if (this.filterFit) list = list.filter((p) => (p.fit || '') === this.filterFit);

      if (this.filterStyle) {
        list = list.filter((p) => (p.style || '').toLowerCase() === this.filterStyle.toLowerCase());
      }

      if (this.filterSize) {
        list = list.filter((p) => (p.variants || []).some((v) => {
          const size = (((v.size || '') || (parseVariantTitle(v.title)[0] || ''))).toLowerCase();
          return size === this.filterSize.toLowerCase();
        }));
      }

      if (this.filterColor) {
        list = list.filter((p) => (p.variants || []).some((v) => {
          const color = (((v.color || '') || (parseVariantTitle(v.title)[1] || ''))).toLowerCase();
          return color === this.filterColor.toLowerCase();
        }));
      }

      return list;
    },

    // -------------------------
    // quick select -> slot fill
    // -------------------------
    openQuickSelect(product) {
      this.quickSelectProduct = product;
    },

    selectVariant(variant, product) {
      if (!variant || !variant.available) return;

      const firstEmpty = this.slots.findIndex((s) => !s);
      if (firstEmpty === -1) return;

      // Prevent duplicates (same variant twice)
      const exists = this.slots.some((s) => s && String(s.variantId) === String(variant.id));
      if (exists) {
        this.quickSelectProduct = null;
        return;
      }

      const entry = {
        variantId: variant.id,
        productTitle: product.title,
        productId: product.id,
        image_url: variant.image_url || product.featured_image || '',
        title: variant.title,
        price: variant.price,
        available: variant.available
      };

      this.slots = this.slots.map((s, i) => (i === firstEmpty ? entry : s));
      this.quickSelectProduct = null;
    },

    // -------------------------
    // add bundle to cart (V1 native)
    // -------------------------
    async addBundleToCart() {
      const filled = this.slots.filter(Boolean);

      console.log('[ALPHX Bundle Builder] Add to cart clicked:', filled.length, '/', this.tier);

      if (filled.length < this.tier) return;

      this.checkoutLoading = true;

      try {
        // Line item properties are OPTIONAL; safe to keep for grouping/analytics
        const propertiesBase = {
          _alphx_bundle_id: this.bundleId,
          _alphx_bundle_tier: String(this.tier),
          _alphx_bundle_source: 'bundle-builder-v1'
        };

        const items = filled.map((s) => ({
          id: Number(s.variantId),
          quantity: 1,
          properties: {
            ...propertiesBase,
            _alphx_bundle_role: 'bundle_item'
          }
        }));

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items }),
          credentials: 'same-origin'
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('[ALPHX Bundle Builder] Cart add failed:', res.status, text);
          throw new Error(text || 'Cart add failed');
        }

        const data = await res.json();
        console.log('[ALPHX Bundle Builder] Bundle added:', data);

        // Clear builder for next bundle
        this.clearBundle();

        // Refresh Be Yours cart UI (if theme listens)
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Guaranteed fallback: take customer to cart to see items + automatic discount
        setTimeout(() => { window.location.href = '/cart'; }, 250);

      } catch (e) {
        console.error('[ALPHX Bundle Builder] Add to cart failed:', e);
        alert(e.message || 'Something went wrong. Try again.');
      } finally {
        this.checkoutLoading = false;
      }
    },

    // -------------------------
    // utilities
    // -------------------------
    newBundleId() {
      return `alx_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  };
}

function parseVariantTitle(title) {
  if (!title || typeof title !== 'string') return ['', ''];
  const parts = title
    .split(/\s*\/\s*|\s*-\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  return [parts[0] || '', parts[1] || ''];
}

/* ------------------------------------------------------------
   Alpine registration (RACE-CONDITION SAFE)
   - Registers immediately if Alpine already exists
   - Otherwise registers on alpine:init
------------------------------------------------------------ */
(function registerBundleBuilder() {
  if (window.Alpine && typeof window.Alpine.data === 'function') {
    window.Alpine.data('bundleBuilder', bundleBuilder);
    return;
  }
  document.addEventListener('alpine:init', () => {
    Alpine.data('bundleBuilder', bundleBuilder);
  });
})();
